import { motion } from "framer-motion";
import {
    Instagram,
    Zap,
    Mic,
    Plane,
    Clapperboard,
    Scissors,
    ArrowUpRight,
} from "lucide-react";
import { useI18n } from "../../lib/i18n";

const icons = [Instagram, Zap, Mic, Plane, Clapperboard, Scissors];

const cardVariant = {
    hidden: { opacity: 0, y: 30 },
    show: (i) => ({
        opacity: 1,
        y: 0,
        transition: { duration: 0.85, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] },
    }),
};

export default function OurServices() {
    const { t } = useI18n();
    const s = t.ourServices;
    return (
        <section
            id="our-services"
            className="section relative bg-[#050505]"
            data-testid="our-services-section"
        >
            <div className="container-x">
                <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-16 md:mb-20">
                    <div className="max-w-3xl">
                        <div className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.28em] text-[#E55B13] mb-6">
                            <span className="w-8 h-px bg-[#E55B13]" />
                            {s.eyebrow}
                        </div>
                        <h2 className="font-heading text-4xl md:text-6xl lg:text-7xl font-light tracking-[-0.03em] leading-[1.02]">
                            {s.titlePre}
                            <span className="italic font-extralight text-[#E55B13]">
                                {s.titleAccent}
                            </span>
                            {s.titlePost}
                        </h2>
                    </div>
                    <p className="font-body text-white/55 max-w-sm text-base leading-relaxed">
                        {s.lede}
                    </p>
                </div>

                <div
                    className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
                    data-testid="our-services-grid"
                >
                    {s.cards.map((c, i) => {
                        const Icon = icons[i] || Clapperboard;
                        const n = String(i + 1).padStart(2, "0");
                        return (
                            <motion.a
                                key={i}
                                href="#services"
                                custom={i}
                                initial="hidden"
                                whileInView="show"
                                viewport={{ once: true, margin: "-80px" }}
                                variants={cardVariant}
                                className="service-card group relative p-8 md:p-10 min-h-[300px] flex flex-col justify-between overflow-hidden"
                                data-testid={`our-service-card-${n}`}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-12 h-12 rounded-2xl bg-white/[0.04] border border-white/10 flex items-center justify-center text-white/85 group-hover:text-[#E55B13] group-hover:border-[#E55B13]/40 transition-colors duration-500">
                                        <Icon size={20} strokeWidth={1.5} />
                                    </div>
                                    <span className="font-mono text-[11px] tracking-[0.28em] text-white/30">
                                        {n}
                                    </span>
                                </div>

                                <div className="mt-12">
                                    <h3 className="font-heading text-xl md:text-2xl font-medium tracking-[-0.02em] text-white leading-snug">
                                        {c.title}
                                    </h3>
                                    <p className="mt-3 font-body text-[14px] md:text-[15px] text-white/60 leading-relaxed">
                                        {c.body}
                                    </p>
                                    <div className="mt-6 inline-flex items-center gap-2 text-white/70 group-hover:text-[#E55B13] transition-colors">
                                        <span className="font-mono text-[11px] tracking-[0.22em] uppercase">
                                            {s.explore}
                                        </span>
                                        <ArrowUpRight
                                            size={14}
                                            className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform"
                                        />
                                    </div>
                                </div>
                            </motion.a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
